import { useCallback, useRef, useState } from 'react';
import { ColumnsType } from 'antd/lib/table';
import cloneDeep from 'lodash.clonedeep';
import moment from 'moment';
import { exportReportToExcel } from '@core/utils/methods/export-report-to-excel';
import { exportReportToPdf } from '@core/utils/methods/export-report-to-pdf';
import {
  ReportExportMetric,
  ReportExportScope,
} from '@core/utils/methods/report-export-table-data';

interface ReportExportOptions<T> {
  columns: ColumnsType<T>;
  metrics: ReportExportMetric[];
  reportTitle: string;
  rows: T[];
  scope: ReportExportScope;
  chartsRef?: { readonly current: HTMLDivElement | null };
  chartsMode?: 'required' | 'none';
}

export const useReportExport = <T>({
  chartsRef,
  ...options
}: ReportExportOptions<T>) => {
  const busy = useRef(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const run = useCallback(
    async (format: 'excel' | 'pdf'): Promise<void> => {
      if (busy.current) return;

      busy.current = true;
      setExporting(true);
      setExportError(undefined);

      const snapshot = {
        ...options,
        rows: cloneDeep(options.rows),
        scope: cloneDeep(options.scope),
        metrics: [...options.metrics],
      };

      try {
        const fileName = `${options.reportTitle.replace(/[^a-z0-9-]/gi, '-')}-${
          snapshot.scope.asOfDate ?? moment().format('YYYY-MM-DD-HHmm')
        }`;

        if (format === 'excel') {
          await exportReportToExcel({
            ...snapshot,
            fileName: `${fileName}.xlsx`,
          });
        } else {
          await exportReportToPdf({
            ...snapshot,
            chartsElement: chartsRef?.current ?? null,
            fileName: `${fileName}.pdf`,
          });
        }
      } catch {
        setExportError(
          options.chartsMode === 'none'
            ? 'Export failed. Please retry.'
            : 'Export failed. Please retry; PDF export requires all charts to be captured.',
        );
      } finally {
        busy.current = false;
        setExporting(false);
      }
    },
    [chartsRef, options],
  );

  return {
    exporting,
    exportError,
    onExportExcel: (): void => {
      run('excel');
    },
    onExportPdf: (): void => {
      run('pdf');
    },
  };
};
